import * as React from 'react';
import { Control, Errors, Form } from 'react-redux-form';
import { VendorViewModel, VendorSearchViewModel } from '@models/Vendor';
import { getDefaultErrorMessages, isValidPhoneNumber } from '../../../../../common/validators';
import { hasValue, isValidEmail, hasAlpha } from '@common/validators';
import { parsePhoneNumber } from '../../../../../common/parsers';
import FormNames from '@constants/FormNames.enum';
import { CheckboxInline, ModalProps } from '@common/components';
import { VendorDuplicateReviewContainer } from './vendor-duplicate-review.container';

export interface GeneralInfoComponentProperties {
  readonly vendorVM: VendorViewModel;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly onSaveClick: (model: VendorViewModel, validator: any, isEdit?: boolean) => void;
  readonly isVerifying?: boolean;
  readonly searchVendors: (
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string
  ) => Promise<any>;
  readonly vendorSearchResults: VendorSearchViewModel[];
  readonly vendorSearchPageCount: number;
  readonly isEdit?: boolean;
  readonly toggleModal: (props?: ModalProps) => void;
  readonly clearVendors: () => void;
}

export interface GeneralInfoComponentState {
  ignoreDuplicates: boolean;
}

export class GeneralInfoComponent extends React.Component<GeneralInfoComponentProperties, GeneralInfoComponentState> {
  constructor(props: GeneralInfoComponentProperties) {
    super(props);
    this.state = {
      ignoreDuplicates: false
    };

    this.verifyDuplicate = this.verifyDuplicate.bind(this);
    this.checkDuplicate = this.checkDuplicate.bind(this);
  }

  verifyDuplicate = async (formValues: VendorViewModel): Promise<any> => {
    try {
      await this.checkDuplicate(formValues);
      if (this.state.ignoreDuplicates) {
        return true;
      }
      const hasDuplicates = (this.props.vendorSearchResults && this.props.vendorSearchResults.length > 0) as boolean;
      if (formValues.companyName) {
        if (hasDuplicates) {
          this.props.toggleModal({
            title: 'Duplicate Vendors',
            component: VendorDuplicateReviewContainer,
            hideX: true,
            saveLabel: 'Continue Vendor Creation',
            closeLabel: 'Cancel',
            saveCallback: () => {
              this.props.toggleModal();
              this.setState({
                ignoreDuplicates: true
              });
            },
            childProps: {
              vendorName: formValues.companyName
            }
          });
        }
      }
      return !hasDuplicates;
    } catch (err) {
      return false;
    }
  };

  componentWillUnmount() {
    this.props.clearVendors();
  }

  checkDuplicate = (formValues: VendorViewModel) => {
    let vendorName: string = '';
    let vendorNumber: string = '';
    if (formValues.companyName && formValues.businessPhone) {
      vendorName = formValues.companyName;
      vendorNumber = formValues.businessPhone!;
    }
    return this.props.searchVendors(`${vendorNumber} ${vendorName}`, 50, 1, 'id', 'asc');
  };

  isVerifying = (): boolean => {
    return !this.props.isVerifying || false;
  };

  renderHours() {
    const hours = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
    return hours.map((option, index) => {
      return (
        <option key={index} value={option || ''}>
          {option}
        </option>
      );
    });
  }

  renderMinutes() {
    const minutes = ['00', '15', '30', '45'];
    return minutes.map((option, index) => {
      return (
        <option key={index} value={option || ''}>
          {option}
        </option>
      );
    });
  }

  render() {
    return (
      <div>
        {!this.props.vendorVM.id && <h2>Step 1 - Vendor Profile - General Info</h2>}
        <Form
          model={FormNames.VendorGeneralInfo}
          hideNativeErrors={true}
          onSubmit={e => this.props.onSaveClick(e, this.verifyDuplicate, this.props.isEdit)}
        >
          <div className="row middle-xs">
            <div className="form-control col-md-6">
              <label htmlFor=".companyName">Company Name</label>
              <Control.text
                model=".companyName"
                maxLength={40}
                validators={{ required: hasValue, hasAlpha }}
                tabIndex={0}
              />
              <Errors
                className="error error-pad"
                model=".companyName"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-6">
              <label htmlFor=".emailAddress">Vendor's Email Address</label>
              <Control.text model=".emailAddress" maxLength={50} validators={{ required: hasValue, isValidEmail }} />
              <Errors
                className="error error-pad"
                model=".emailAddress"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          {
            //////////////
          }
          <div className="row middle-xs">
            <div className="form-control col-xs-6">
              <label htmlFor=".dbaName">DBA Name</label>
              <Control.text model=".dbaName" maxLength={40} validators={{ required: hasValue, hasAlpha }} />
              <Errors
                className="error error-pad"
                model=".dbaName"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-2">
              <label htmlFor=".businessPhone">Business Phone</label>
              <Control.text
                model=".businessPhone"
                parser={parsePhoneNumber}
                validators={{ required: hasValue, isValidPhoneNumber }}
              />
              <Errors
                className="error error-pad"
                model=".businessPhone"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-2">
              <label htmlFor=".businessFax">Business Fax</label>
              <Control.text model=".businessFax" parser={parsePhoneNumber} validators={{ isValidPhoneNumber }} />
              <Errors
                className="error error-pad"
                model=".businessFax"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-2">
              <label htmlFor=".afterHoursPhone">After Hours</label>
              <Control.text model=".afterHoursPhone" parser={parsePhoneNumber} validators={{ isValidPhoneNumber }} />
              <Errors
                className="error error-pad"
                model=".afterHoursPhone"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          {
            ///////
          }
          <div className="row middle-xs">
            <div className="form-control col-md-6">
              <label htmlFor=".ownerName">Owner</label>
              <Control.text model=".ownerName" maxLength={30} validators={{ required: hasValue, hasAlpha }} />
              <Errors
                className="error error-pad"
                model=".ownerName"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>

            <div className="form-control col-md-6">
              <label htmlFor=".managerName">Manager</label>
              <Control.text model=".managerName" maxLength={30} validators={{ hasAlpha }} />
              <Errors
                className="error error-pad"
                model=".managerName"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>

          <div className="row middle-xs">
            <div className="form-control col-md-12">
              <label htmlFor=".ownerName">Business Hours</label>
              <div className="row form-control">
                <div className="col-md-1">
                  <h3>M - F</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".weekdayHours.startHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".weekdayHours.startMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".weekdayHours.startPeriod" value="AM" />
                    <label htmlFor=".weekdayHours.startPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".weekdayHours.startPeriod" value="PM" />
                    <label htmlFor=".weekdayHours.startPeriod">PM</label>
                  </CheckboxInline>
                </div>
                <div className="col-md-1 form-control">
                  <h3>TO</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".weekdayHours.endHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".weekdayHours.endMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".weekdayHours.endPeriod" value="AM" />
                    <label htmlFor=".weekdayHours.endPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".weekdayHours.endPeriod" value="PM" />
                    <label htmlFor=".weekdayHours.endPeriod">PM</label>
                  </CheckboxInline>
                </div>
              </div>
              <div className="row form-control">
                <div className="col-md-1">
                  <h3>Saturday</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".saturdayHours.startHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".saturdayHours.startMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".saturdayHours.startPeriod" value="AM" />
                    <label htmlFor=".saturdayHours.startPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".saturdayHours.startPeriod" value="PM" />
                    <label htmlFor=".saturdayHours.startPeriod">PM</label>
                  </CheckboxInline>
                </div>
                <div className="col-md-1 form-control">
                  <h3>TO</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".saturdayHours.endHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".saturdayHours.endMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".saturdayHours.endPeriod" value="AM" />
                    <label htmlFor=".saturdayHours.endPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".saturdayHours.endPeriod" value="PM" />
                    <label htmlFor=".saturdayHours.endPeriod">PM</label>
                  </CheckboxInline>
                </div>
              </div>
              <div className="row form-control">
                <div className="col-md-1">
                  <h3>Sunday</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".sundayHours.startHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".sundayHours.startMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".sundayHours.startPeriod" value="AM" />
                    <label htmlFor=".sundayHours.startPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".sundayHours.startPeriod" value="PM" />
                    <label htmlFor=".sundayHours.startPeriod">PM</label>
                  </CheckboxInline>
                </div>
                <div className="col-md-1 form-control">
                  <h3>TO</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".sundayHours.endHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".sundayHours.endMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".sundayHours.endPeriod" value="AM" />
                    <label htmlFor=".sundayHours.endPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".sundayHours.endPeriod" value="PM" />
                    <label htmlFor=".sundayHours.endPeriod">PM</label>
                  </CheckboxInline>
                </div>
              </div>
              <div className="row form-control">
                <div className="col-md-1">
                  <h3>Holiday</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".holidayHours.startHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".holidayHours.startMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".holidayHours.startPeriod" value="AM" />
                    <label htmlFor=".holidayHours.startPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".holidayHours.startPeriod" value="PM" />
                    <label htmlFor=".holidayHours.startPeriod">PM</label>
                  </CheckboxInline>
                </div>
                <div className="col-md-1 form-control">
                  <h3>TO</h3>
                </div>
                <div className="col-md-2">
                  <Control.select model=".holidayHours.endHour">
                    <option value="">HH</option>
                    {this.renderHours()}
                  </Control.select>
                </div>
                <div className="col-md-2">
                  <Control.select model=".holidayHours.endMinute">
                    <option value="">MM</option>
                    {this.renderMinutes()}
                  </Control.select>
                </div>
                <div className="col-md-1">
                  <CheckboxInline>
                    <Control.radio model=".holidayHours.endPeriod" value="AM" />
                    <label htmlFor=".holidayHours.endPeriod">AM</label>
                  </CheckboxInline>
                  <CheckboxInline>
                    <Control.radio model=".holidayHours.endPeriod" value="PM" />
                    <label htmlFor=".holidayHours.endPeriod">PM</label>
                  </CheckboxInline>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-1">
            <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
              Save
            </button>
          </div>
        </Form>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/vendor/_components/flows/steps/general-info/general-info.component.tsx